import React, { useState } from "react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { promoterApi } from "../../api/client";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";

const CreateProviderSimple = () => {
  const [formData, setFormData] = useState({
    email: "",
    phoneNumber: "",
    password: "",
    companyName: "",
    confirmPassword: "",
  });
  const [loading, setLoading] = useState(false);
  const [generalError, setGeneralError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value, // Don't trim during typing - allow spaces
    }));
  };

  // Submit form
  const handleSubmit = async (e) => {
    e.preventDefault();
    setGeneralError("");

    // Trim values only on submit
    const trimmedData = {
      email: formData.email.trim(),
      phoneNumber: formData.phoneNumber.trim(),
      companyName: formData.companyName.trim(),
      password: formData.password,
      confirmPassword: formData.confirmPassword,
    };

    if (!trimmedData.email) {
      alert("Email is required!");
      return;
    }
    if (!trimmedData.phoneNumber) {
      alert("Mobile number is required!");
      return;
    }
    if (trimmedData.password !== trimmedData.confirmPassword) {
      alert("Passwords do not match!");
      return;
    }
    if (!trimmedData.phoneNumber.startsWith("+")) {
      alert("Please enter a valid mobile number.");
      return;
    }

    try {
      setLoading(true);

      // Create basic provider account
      const data = await promoterApi.createProvider({
        email: trimmedData.email,
        password: trimmedData.password,
        companyName: trimmedData.companyName,
        phoneNumber: trimmedData.phoneNumber,
      });

      // Show success message and redirect
      alert(
        "Provider account created successfully! They can now log in to complete their profile."
      );
      navigate("/promoter/providers");
    } catch (error) {
      console.error("Error creating provider:", error);
      const status = error?.status;
      const data = error?.data || {};
      if (data?.code === "VALIDATION_ERROR") {
        const first =
          Array.isArray(data.errors) && data.errors.length > 0
            ? data.errors[0]?.message
            : null;
        setGeneralError(first || data?.message || "Validation error");
      } else if (status === 409 || data?.code === "CONFLICT") {
        setGeneralError(
          data?.message || "Email or phone number already exists"
        );
      } else if (status === 401 || data?.code === "UNAUTHORIZED") {
        setGeneralError(
          "You need to be an approved promoter to create providers. Please contact your channel partner or admin for approval."
        );
      } else {
        setGeneralError(data?.message || "Provider creation failed");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-xl mx-auto px-6 py-10 poppins-regular">
      <h2 className="text-3xl poppins-bold uppercase text-center mb-8 text-gray-800">
        Create Provider
      </h2>

      {/* Information note */}
      <div className="mb-6 text-center">
        <span className="text-lg font-medium text-gray-700">
          Create a basic provider account. They can log in later to complete
          their profile.
        </span>
        <div className="mt-2 text-sm text-orange-600">
          <strong>Note:</strong> You need to be an approved promoter to create
          providers.
        </div>
      </div>

      {generalError && (
        <div className="mb-4 p-3 rounded bg-red-50 text-red-700 border border-red-200">
          {generalError}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Company Name */}
        <div>
          <label className="block text-sm font-semibold uppercase text-gray-600 mb-1">
            Company Name
          </label>
          <input
            type="text"
            name="companyName"
            required
            value={formData.companyName}
            onChange={handleChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
            placeholder="Enter company name"
          />
        </div>

        {/* Email */}
        <div>
          <label className="block text-sm font-semibold uppercase text-gray-600 mb-1">
            Email
          </label>
          <input
            type="email"
            name="email"
            required
            value={formData.email}
            onChange={handleChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
            placeholder="Enter email address"
          />
        </div>

        {/* Mobile */}
        <div>
          <label className="block text-sm font-semibold uppercase text-gray-600 mb-1">
            Mobile Number
          </label>
          <PhoneInput
            country={"in"}
            value={formData.phoneNumber}
            onChange={(value) =>
              setFormData((prev) => ({ ...prev, phoneNumber: `+${value}` }))
            }
            inputStyle={{
              width: "100%",
              height: "42px",
              fontSize: "16px",
              borderRadius: "0.5rem",
              border: "1px solid #d1d5db",
            }}
            buttonStyle={{
              borderRadius: "0.5rem 0 0 0.5rem",
              border: "1px solid #d1d5db",
              backgroundColor: "#fff",
            }}
            placeholder="Enter mobile number"
          />
        </div>

        {/* Password */}
        <div>
          <label className="block text-sm font-semibold uppercase text-gray-600 mb-1">
            Password
          </label>
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              name="password"
              required
              minLength={8}
              value={formData.password}
              onChange={handleChange}
              className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder="Enter password (min 8 characters)"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
            >
              {showPassword ? (
                <EyeOff className="h-5 w-5" />
              ) : (
                <Eye className="h-5 w-5" />
              )}
            </button>
          </div>
        </div>

        {/* Confirm Password */}
        <div>
          <label className="block text-sm font-semibold uppercase text-gray-600 mb-1">
            Confirm Password
          </label>
          <div className="relative">
            <input
              type={showConfirmPassword ? "text" : "password"}
              name="confirmPassword"
              required
              minLength={8}
              value={formData.confirmPassword}
              onChange={handleChange}
              className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder="Confirm password"
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
            >
              {showConfirmPassword ? (
                <EyeOff className="h-5 w-5" />
              ) : (
                <Eye className="h-5 w-5" />
              )}
            </button>
          </div>
        </div>

        {/* Submit */}
        <div className="text-right">
          <button
            type="submit"
            disabled={loading}
            className={`bg-gradient-to-r from-blue-500 to-blue-700 uppercase px-6 py-2 text-white rounded-lg mulish-bold transition ${
              loading ? "opacity-50 cursor-not-allowed" : "hover:text-blue-200"
            }`}
          >
            {loading ? "Creating..." : "Create Provider"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateProviderSimple;
